import api from './api';
import {uploadFilesSequential} from '@/utils/fileUploadQueue';

export default {
    actions: {
        /**
         * Получить счетчики заявок для всех вкладок
         */
        async requests_getCounts({commit}) {
            try {
                const counts = await api.getRequestCounts();
                commit('requests_setCounts', counts || { my: 0, requires_action: 0, all: 0, all_employees: 0 });
                return counts;
            } catch (error) {
                console.error('Ошибка при загрузке счетчиков заявок:', error);
                throw error;
            }
        },

        /**
         * Получить список заявок с пагинацией.
         * @param {Object} payload - { tab, page, resetPage }
         */
        async requests_getRequests({commit, state}, payload = {}) {
            const tab = payload.tab || null;
            const page = payload.resetPage ? 1 : (payload.page || state.listPage);

            if (payload.resetPage) {
                commit('requests_setListPage', 1);
            }

            commit('requests_setLoading', true);
            try {
                const data = await api.getRequests({
                    tab,
                    page,
                    per_page: state.listPerPage,
                    sort_by: state.sortBy,
                    sort_desc: state.sortDesc,
                    q: state.filter,
                    status: state.statusFilter,
                    recurrence_kind: state.recurrenceFilter,
                    request_type_id: state.requestTypeFilter,
                    building_process_id: state.buildingProcessFilter
                });
                commit('requests_setItems', (data && data.items) || []);
                commit('requests_setBadgeFilterOptions', (data && data.badge_filter_options) || []);
                commit('requests_setListTotal', data && data.total != null ? data.total : 0);
                commit('requests_setListPage', data && data.page ? data.page : page);
                return data;
            } catch (error) {
                console.error('Ошибка при загрузке заявок:', error);
                throw error;
            } finally {
                commit('requests_setLoading', false);
            }
        },

        /**
         * Получить заявку по ID
         */
        requests_getRequest(_, id) {
            return api.getRequest(id);
        },

        /**
         * Создать новую заявку
         */
        requests_createRequest(_, payload) {
            return api.createRequest(payload);
        },

        /**
         * Загрузить вложения к существующей заявке по одному файлу.
         */
        requests_uploadRequestFiles(_, {id, files}) {
            return uploadFilesSequential(
                files,
                file => api.uploadRequestFiles(id, [file])
            );
        },

        /**
         * Загрузить обязательные вложения к существующей заявке по одному файлу.
         */
        requests_uploadRequiredAttachment(_, {id, fileKind, files}) {
            return uploadFilesSequential(
                files,
                file => api.uploadRequiredAttachment(id, fileKind, [file])
            );
        },

        /**
         * Обновить заявку
         */
        requests_updateRequest(_, {id, payload, options}) {
            return api.updateRequest(id, payload, options);
        },

        /**
         * Обновить позицию заявки
         */
        requests_updateRequestItem(_, { requestId, itemId, payload }) {
            return api.updateRequestItem(requestId, itemId, payload);
        },

        /**
         * Удалить заявку
         */
        requests_deleteRequest(_, id) {
            return api.deleteRequest(id);
        },

        /**
         * Получить типы заявок
         */
        async requests_getRequestTypes({commit}) {
            try {
                const types = await api.getRequestTypes();
                commit('requests_setRequestTypes', types || []);
                return types;
            } catch (error) {
                console.error('Ошибка при загрузке типов заявок:', error);
                throw error;
            }
        },

        /**
         * Получить один тип заявки по ID
         */
        requests_getRequestType(_, id) {
            return api.getRequestType(id);
        },

        /**
         * Получить типы заявок для фильтра списка.
         */
        async requests_getRequestTypeFilterOptions({commit}) {
            try {
                const types = await api.getRequestTypeFilterOptions();
                commit('requests_setRequestTypeFilterOptions', types || []);
                return types;
            } catch (error) {
                console.error('Ошибка при загрузке типов заявок для фильтра:', error);
                throw error;
            }
        },

        /**
         * Получить объекты для фильтра списка заявок.
         */
        async requests_getBuildingProcessFilterOptions({commit}) {
            try {
                const processes = await api.getRequestBuildingProcessFilterOptions();
                commit('requests_setBuildingProcessFilterOptions', processes || []);
                return processes;
            } catch (error) {
                console.error('Ошибка при загрузке объектов для фильтра:', error);
                throw error;
            }
        },

        /**
         * Создать тип заявки
         */
        requests_createRequestType(_, payload) {
            return api.createRequestType(payload);
        },

        /**
         * Обновить тип заявки
         */
        requests_updateRequestType(_, {id, payload}) {
            return api.updateRequestType(id, payload);
        },

        /**
         * Удалить тип заявки
         */
        requests_deleteRequestType(_, id) {
            return api.deleteRequestType(id);
        },

        /**
         * Согласовать заявку
         */
        requests_approveRequest(_, payload) {
            const id = payload && typeof payload === 'object' ? payload.id : payload;
            const comment = payload && typeof payload === 'object' ? payload.comment : null;
            return api.approveRequest(id, comment);
        },

        /**
         * Отклонить заявку
         * @param {Object} context - Vuex context
         * @param {Object} payload - Параметры
         * @param {number} payload.id - ID заявки
         * @param {string} payload.comment - Причина отказа (обязательно)
         */
        requests_rejectRequest(_, { id, comment }) {
            return api.rejectRequest(id, comment);
        },

        /**
         * Взять заявку в работу
         */
        requests_executeRequest(_, id) {
            return api.executeRequest(id);
        },

        /**
         * Отправить заявку на согласование
         */
        requests_submitRequest(_, id) {
            return api.submitRequest(id);
        },

        /**
         * Вернуть заявку на доработку
         * @param {Object} context - Vuex context
         * @param {Object} payload - Параметры
         * @param {number} payload.id - ID заявки
         * @param {string} payload.comment - Причина возврата (обязательно)
         */
        requests_returnRequest(_, { id, comment }) {
            return api.returnRequest(id, comment);
        },

        /**
         * Завершить выполнение заявки
         */
        requests_completeRequest(_, id) {
            return api.completeRequest(id);
        },

        /**
         * Принять выполненную заявку
         */
        requests_acceptRequest(_, id) {
            return api.acceptRequest(id);
        },

        /**
         * Частично выполнить заявку
         */
        requests_partiallyCompleteRequest(_, id) {
            return api.partiallyCompleteRequest(id);
        },

        /**
         * Принять частично выполненную заявку
         */
        requests_acceptPartiallyRequest(_, id) {
            return api.acceptPartiallyRequest(id);
        },

        /**
         * Отменить заявку
         */
        requests_cancelRequest(_, payload) {
            const id = payload && typeof payload === 'object' ? payload.id : payload;
            const comment = payload && typeof payload === 'object' ? payload.comment : null;
            return api.cancelRequest(id, comment);
        },

        /**
         * Изменить статус заявки напрямую (только для администратора)
         */
        requests_changeRequestStatus(_, { id, targetStatus, comment }) {
            return api.changeRequestStatus(id, targetStatus, comment);
        },

        /**
         * Назначить исполнителя на заявку
         */
        requests_assignRequest(_, { id, executorId }) {
            return api.assignRequest(id, executorId);
        },

        /**
         * Получить список доступных исполнителей для ручного назначения
         * @param {number|string} requestId - ID заявки
         */
        requests_getAvailableExecutors(_, requestId) {
            return api.getAvailableExecutors(requestId);
        },

        /**
         * Обновить отгруженное количество для материала (одиночное)
         */
        requests_shipMaterialItem(_, { requestId, itemId, shipped_quantity, shipping_comment }) {
            return api.shipMaterialItem(requestId, itemId, shipped_quantity, shipping_comment);
        },

        /**
         * Массовое обновление отгруженных количеств
         */
        requests_bulkShipItems(_, { requestId, items }) {
            return api.bulkShipMaterialItems(requestId, items);
        },

        /**
         * Обновить принятое количество для материала (одиночное)
         */
        requests_acceptMaterialItem(_, { requestId, itemId, accepted_quantity, acceptance_comment }) {
            return api.acceptMaterialItem(requestId, itemId, accepted_quantity, acceptance_comment);
        },

        /**
         * Массовое обновление принятых количеств
         */
        requests_bulkAcceptItems(_, { requestId, items }) {
            return api.bulkAcceptMaterialItems(requestId, items);
        },

        /**
         * Массовое обновление материалов (добавление/обновление/удаление)
         */
        requests_bulkUpdateMaterials(_, { requestId, items }) {
            return api.bulkUpdateMaterials(requestId, items);
        },

        /**
         * Получить доступные строительные процессы с фильтрацией по ролям
         * @param {number} [requestTypeId] - ID типа заявки (опционально)
         */
        requests_getAvailableBuildingProcesses(_, requestTypeId = null) {
            return api.getAvailableBuildingProcesses(requestTypeId);
        },

        /**
         * Получить справочник материалов без проверки прав доступа
         */
        requests_getMaterials(_, requestTypeId = null) {
            return api.getMaterials(requestTypeId);
        },

        /**
         * Получить подключенный к типу заявки справочник позиций
         */
        requests_getItemCatalog(_, payload) {
            const requestTypeId = payload && typeof payload === 'object'
                ? payload.requestTypeId
                : payload;
            const options = payload && typeof payload === 'object'
                ? payload
                : {};
            return api.getItemCatalog(requestTypeId, options);
        },

        /**
         * Получить черновик для типа заявки
         */
        async requests_getDraft({ commit }, requestTypeId) {
            try {
                const draft = await api.getDraft(requestTypeId);
                commit('requests_setAvailableDraft', draft);
                return draft;
            } catch (error) {
                console.error('Ошибка получения черновика:', error);
                throw error;
            }
        },

        /**
         * Сохранить черновик
         */
        async requests_saveDraft({ commit }, { requestTypeId, draftData }) {
            try {
                const result = await api.saveDraft(requestTypeId, draftData);
                commit('requests_setCurrentDraft', result.draft);
                return result;
            } catch (error) {
                console.error('Ошибка сохранения черновика:', error);
                throw error;
            }
        },

        /**
         * Удалить черновик
         */
        async requests_deleteDraft({ commit }, draftId) {
            try {
                await api.deleteDraft(draftId);
                commit('requests_clearDraft');
                return { success: true };
            } catch (error) {
                console.error('Ошибка удаления черновика:', error);
                throw error;
            }
        },

        /**
         * Удалить черновик по типу заявки
         */
        async requests_deleteDraftByType({ commit }, requestTypeId) {
            try {
                await api.deleteDraftByType(requestTypeId);
                commit('requests_clearDraft');
                return { success: true };
            } catch (error) {
                console.error('Ошибка удаления черновика:', error);
                throw error;
            }
        },

        /**
         * Копировать заявку
         */
        async requests_copyRequest(_, id) {
            try {
                const result = await api.copyRequest(id);
                return result;
            } catch (error) {
                console.error('Ошибка копирования заявки:', error);
                throw error;
            }
        }
    },

    state: {
        // Список заявок текущей страницы
        items: [],
        // Индикатор загрузки
        loading: false,
        // Пагинация
        listTotal: 0,
        listPage: 1,
        listPerPage: 20,
        // Счетчики заявок для вкладок
        counts: {
            my: 0,
            requires_action: 0,
            all: 0,
            all_employees: 0
        },
        // Параметры сортировки
        sortBy: 'status',
        sortDesc: false,
        sortOptions: [
            {text: 'По дате создания', value: 'createdAt'},
            {text: 'По статусу', value: 'status'},
            {text: 'По приоритету', value: 'priority'},
            {text: 'По названию', value: 'title'},
            {text: 'По дате срока исполнения', value: 'deadline'}
        ],
        // Фильтр поиска
        filter: '',
        // Фильтр по статусу
        statusFilter: 'all',
        recurrenceFilter: '',
        // Фильтр по типу заявки
        requestTypeFilter: 'all',
        // Фильтр по объекту
        buildingProcessFilter: 'all',
        // Выбранная вкладка
        selectedTab: 0,
        // Типы заявок
        requestTypes: [],
        // Типы заявок для фильтра списка
        requestTypeFilterOptions: [],
        // Объекты для фильтра списка
        buildingProcessFilterOptions: [],
        // Badge-фильтры по всей серверной выборке (без ограничения текущей страницей)
        badgeFilterOptions: [],
        // Данные формы создания заявки
        createForm: {
            request_type_id: null,
            title: '',
            description: '',
            priority: 'normal',
            deadline: null,
            urgency_comment: '',
            building_process_id: null,
            selectedMaterials: [],
            attachments: []
        },
        // Текущий активный черновик (сохраненный в процессе работы)
        currentDraft: null,
        // Доступный черновик для восстановления (загруженный с сервера)
        availableDraft: null
    },

    mutations: {
        /**
         * Установить список заявок
         */
        requests_setItems(state, items) {
            state.items = items;
        },

        requests_setListTotal(state, total) {
            state.listTotal = total;
        },

        requests_setListPage(state, page) {
            state.listPage = page;
        },

        requests_setListPerPage(state, perPage) {
            state.listPerPage = perPage;
        },

        /**
         * Установить индикатор загрузки
         */
        requests_setLoading(state, loading) {
            state.loading = loading;
        },

        /**
         * Установить параметр сортировки
         */
        requests_setSortBy(state, sortBy) {
            state.sortBy = sortBy;
        },

        /**
         * Установить направление сортировки
         */
        requests_setSortDesc(state, sortDesc) {
            state.sortDesc = sortDesc;
        },

        /**
         * Установить фильтр поиска
         */
        requests_setFilter(state, filter) {
            state.filter = filter;
        },

        /**
         * Установить фильтр по статусу
         */
        requests_setRecurrenceFilter(state, value) { state.recurrenceFilter = value; },
        requests_setStatusFilter(state, statusFilter) {
            state.statusFilter = statusFilter;
        },

        /**
         * Установить фильтр по типу заявки
         */
        requests_setRequestTypeFilter(state, requestTypeFilter) {
            state.requestTypeFilter = requestTypeFilter;
        },

        /**
         * Установить фильтр по объекту
         */
        requests_setBuildingProcessFilter(state, buildingProcessFilter) {
            state.buildingProcessFilter = buildingProcessFilter;
        },

        /**
         * Установить выбранную вкладку
         */
        requests_setSelectedTab(state, tab) {
            state.selectedTab = tab;
        },

        /**
         * Установить типы заявок
         */
        requests_setRequestTypes(state, types) {
            state.requestTypes = types;
        },

        /**
         * Установить типы заявок для фильтра списка
         */
        requests_setRequestTypeFilterOptions(state, types) {
            state.requestTypeFilterOptions = types;
        },

        /**
         * Установить объекты для фильтра списка
         */
        requests_setBuildingProcessFilterOptions(state, processes) {
            state.buildingProcessFilterOptions = processes;
        },

        requests_setBadgeFilterOptions(state, options) {
            state.badgeFilterOptions = options;
        },

        /**
         * Установить счетчики заявок
         */
        requests_setCounts(state, counts) {
            state.counts = { ...state.counts, ...counts };
        },

        /**
         * Обновить поле формы создания заявки
         */
        requests_setCreateFormField(state, {field, value}) {
            if (state.createForm.hasOwnProperty(field)) {
                state.createForm[field] = value;
            }
        },

        /**
         * Сбросить форму создания заявки
         */
        requests_resetCreateForm(state) {
            state.createForm = {
                request_type_id: null,
                title: '',
                description: '',
                priority: 'normal',
                deadline: null,
                urgency_comment: '',
                building_process_id: null,
                selectedMaterials: [],
                attachments: []
            };
        },

        /**
         * Установить текущий активный черновик
         */
        requests_setCurrentDraft(state, draft) {
            state.currentDraft = draft;
        },

        /**
         * Установить доступный черновик для восстановления
         */
        requests_setAvailableDraft(state, draft) {
            state.availableDraft = draft;
        },

        /**
         * Очистить черновики
         */
        requests_clearDraft(state) {
            state.currentDraft = null;
            state.availableDraft = null;
        }
    },

    getters: {
        /**
         * Список заявок текущей страницы (уже отсортированных и отфильтрованных сервером)
         */
        requests_allRequests: (state) => {
            return state.items;
        },

        requests_listTotal: (state) => state.listTotal,
        requests_listPage: (state) => state.listPage,
        requests_listPerPage: (state) => state.listPerPage,

        /**
         * Получить счетчики заявок
         */
        requests_counts: (state) => {
            return state.counts;
        }
    }
}

